import {
  ChevronRight,
  ClipboardList,
  FileText,
  FolderKanban,
  FolderTree,
  LayoutGrid,
  Pencil,
  Server,
  Sparkles,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CadrageReadWithTeam,
  DEFAULT_ORG_MODULES,
  Domain,
  ForgeClient,
  Organization,
  OrganizationUiSettings,
  OrganizationUpdate,
  User,
  UserRoles,
} from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import organizationService from '@/services/organizationService';
import domainService from '@/services/domainService';
import useCaseService from '@/services/useCaseService';
import cadrageService from '@/services/cadrageService';
import forgeService from '@/services/forgeService';
import projectService from '@/services/projectService';
import { isNavItemHiddenByExperience } from '@/lib/profileUi';
import { useAuthStore } from '@/stores/authStore';
import { useForgeApiEnabled } from '@/stores/forgeApiStore';
import { OrganizationGeneralInformation } from './OrganizationGeneralInformation';

interface OrganizationOverviewProps {
  organization: Organization;
  canEdit: boolean;
  isEditing: boolean;
  formData: OrganizationUpdate;
  saving: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onFormChange: (data: OrganizationUpdate) => void;
  /** Opens the Members tab; the Members tile is clickable when provided. */
  onOpenMembers?: () => void;
}

interface OrgStats {
  totalUsers: number;
  totalAdmins: number;
  totalDomains: number;
  totalUseCases: number;
  totalCadrages: number;
  totalProjects: number;
}

// Static class names so Tailwind keeps them; the tile count varies with the
// experience, the Projects module and Forge.
const TILE_GRID_COLUMNS: Record<number, string> = {
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'md:grid-cols-3 lg:grid-cols-5',
  6: 'md:grid-cols-3 lg:grid-cols-6',
};

const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  hint?: string;
  onClick?: () => void;
}> = ({ icon, label, value, hint, onClick }) => {
  const content = (
    <>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="mt-0.5 text-2xl font-semibold leading-tight">{value}</div>
        {hint && <div className="mt-0.5 truncate text-xs text-muted-foreground">{hint}</div>}
      </div>
      {onClick && <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />}
    </>
  );
  const className = 'flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4 text-left';
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={`${className} transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
    >
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
};

export const OrganizationOverview: React.FC<OrganizationOverviewProps> = ({
  organization,
  canEdit,
  isEditing,
  formData,
  saving,
  onEdit,
  onSave,
  onCancel,
  onFormChange,
  onOpenMembers,
}) => {
  const { t } = useTranslation();
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const currentOrgId = useAuthStore((s) => s.user?.organization_id ?? null);
  const [stats, setStats] = useState<OrgStats>({
    totalUsers: 0,
    totalAdmins: 0,
    totalDomains: 0,
    totalUseCases: 0,
    totalCadrages: 0,
    totalProjects: 0,
  });
  // The displayed org's settings, not orgUiSettingsStore: God may be viewing another org.
  const [uiSettings, setUiSettings] = useState<OrganizationUiSettings | null>(null);
  const [forgeClient, setForgeClient] = useState<ForgeClient | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [users, domains, settings] = await Promise.all([
          organizationService.getOrganizationUsers(organization.id).catch(() => [] as User[]),
          domainService.getOrganizationDomains(organization.id).catch(() => [] as Domain[]),
          organizationService.getUiSettings(organization.id).catch(() => null),
        ]);
        if (!cancelled) setUiSettings(settings);

        // The projects list is scoped to the caller's org, so it can only count the active one.
        const countProjects =
          organization.id === currentOrgId &&
          (settings?.modules?.projects ?? DEFAULT_ORG_MODULES.projects);
        const projectsPromise = countProjects
          ? projectService.list({ scope: 'all' }).catch(() => [])
          : Promise.resolve([]);

        const useCaseLists = await Promise.all(
          domains.map((d) => useCaseService.list({ domain_id: d.id }).catch(() => []))
        );
        const totalUseCases = useCaseLists.reduce((sum, list) => sum + list.length, 0);

        const cadrageLists = await Promise.all(
          useCaseLists.flat().map((uc) =>
            cadrageService
              .getCadragesByUseCase(uc.id)
              .catch(() => [] as CadrageReadWithTeam[])
          )
        );
        const totalCadrages = cadrageLists.reduce((sum, list) => sum + list.length, 0);

        const totalAdmins = users.filter((u) => u.role === UserRoles.Admin).length;
        const totalProjects = (await projectsPromise).length;

        if (!cancelled) {
          setStats({
            totalUsers: users.length,
            totalAdmins,
            totalDomains: domains.length,
            totalUseCases,
            totalCadrages,
            totalProjects,
          });
        }
      } catch (err) {
        console.error('Error loading organization overview stats:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [organization.id, currentOrgId]);

  useEffect(() => {
    let cancelled = false;
    const fetchClient = async () => {
      if (!forgeApiEnabled || !organization.forge_client_id) {
        setForgeClient(null);
        return;
      }
      try {
        const client = await forgeService.getClient(organization.forge_client_id);
        if (!cancelled) setForgeClient(client);
      } catch (err) {
        console.error('Error fetching Forge client:', err);
      }
    };
    void fetchClient();
    return () => {
      cancelled = true;
    };
  }, [organization.forge_client_id, forgeApiEnabled]);

  const showUseCases = !isNavItemHiddenByExperience(
    'usecases',
    uiSettings?.experience ?? 'etabli'
  );
  const showProjects =
    organization.id === currentOrgId &&
    (uiSettings?.modules?.projects ?? DEFAULT_ORG_MODULES.projects);
  const tileCount = 3 + Number(showUseCases) + Number(showProjects) + Number(forgeApiEnabled);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-5 w-5" />
          {t('organizationOverview.statsTitle', 'Aperçu')}
        </CardTitle>
        {canEdit && !isEditing && (
          <Button variant="outline" size="sm" onClick={onEdit} className="flex items-center gap-2">
            <Pencil className="h-4 w-4" />
            {t('organizationGeneralInfo.edit')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <div className={`grid grid-cols-2 gap-3 ${TILE_GRID_COLUMNS[tileCount]}`}>
          <StatTile
            icon={<Users className="h-5 w-5" />}
            label={t('organizationOverview.members', 'Membres')}
            value={loading ? '…' : stats.totalUsers}
            hint={
              stats.totalAdmins > 0
                ? t('organizationOverview.adminsCount', '{{count}} admin(s)', {
                    count: stats.totalAdmins,
                  })
                : undefined
            }
            onClick={onOpenMembers}
          />
          <StatTile
            icon={<LayoutGrid className="h-5 w-5" />}
            label={t('organizationOverview.domains', 'Domaines')}
            value={loading ? '…' : stats.totalDomains}
          />
          {showUseCases && (
            <StatTile
              icon={<ClipboardList className="h-5 w-5" />}
              label={t('organizationOverview.useCases', "Cas d'usage")}
              value={loading ? '…' : stats.totalUseCases}
            />
          )}
          <StatTile
            icon={<FileText className="h-5 w-5" />}
            label={t('organizationOverview.cadrages', 'Cadrages')}
            value={loading ? '…' : stats.totalCadrages}
          />
          {showProjects && (
            <StatTile
              icon={<FolderKanban className="h-5 w-5" />}
              label={t('organizationOverview.projects', 'Projets')}
              value={loading ? '…' : stats.totalProjects}
            />
          )}
          {forgeApiEnabled && (
            <StatTile
              icon={<Server className="h-5 w-5" />}
              label={t('organizationOverview.forgeClient', 'Client Forge')}
              value={forgeClient ? forgeClient.name : t('organizationOverview.notLinked', 'Non lié')}
            />
          )}
        </div>

        <div className="border-t border-border pt-6">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <FolderTree className="h-4 w-4" />
            {t('organizationOverview.identity', 'Identité')}
          </div>
          <OrganizationGeneralInformation
            organization={organization}
            canEdit={canEdit}
            isEditing={isEditing}
            formData={formData}
            saving={saving}
            onEdit={onEdit}
            onSave={onSave}
            onCancel={onCancel}
            onFormChange={onFormChange}
            bare
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default OrganizationOverview;
